import React, { useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  ActivityIndicator,
  Dimensions,
  Animated,
} from 'react-native';
import { useSelector } from 'react-redux';
import { globalFontStyle, globalStyle } from '../assets';
import { globalConfigurableData } from '../utils/globalConstant';
import { Icon } from '@iconify/react';

const { width, height } = Dimensions.get('window');

const MagicLinkAuthLoader = () => {
  const magicLinkAuthLoading = useSelector(
    (state) => state.login.magicLinkAuthLoading
  );
  
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(0.8)).current;
  const progressAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (magicLinkAuthLoading) {
      // Animate in
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.timing(scaleAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start();

      // Animate progress bar
      Animated.timing(progressAnim, {
        toValue: 1,
        duration: 2000,
        useNativeDriver: false,
      }).start();
    } else {
      // Animate out
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(scaleAnim, {
          toValue: 0.8,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start();
      
      progressAnim.setValue(0);
    }
  }, [magicLinkAuthLoading]);

  if (!magicLinkAuthLoading) {
    return null;
  }
  return (
    <Modal
      visible={magicLinkAuthLoading}
      transparent={true}
      animationType="none"
      statusBarTranslucent={true}
    >
      <Animated.View style={[styles.overlay, { opacity: fadeAnim }]}>
        <Animated.View 
          style={[
            styles.loaderContainer, 
            { 
              opacity: fadeAnim,
              transform: [{ scale: scaleAnim }]
            }
          ]}
        >
          <View style={styles.iconContainer}>
            <Icon 
              icon="mdi:google" 
              width={48} 
              height={48}
              color={globalConfigurableData.primaryTextColor1}
            />
          </View>
          
          <Text style={styles.title}>Signing you in...</Text>
          
          <Text style={styles.subtitle}>
            Please wait while we complete your authentication
          </Text>
          
          <View style={styles.spinnerContainer}>
            <ActivityIndicator 
              size="large" 
              color={globalConfigurableData.primaryTextColor1}
            />
          </View>
          
          <View style={styles.progressContainer}>
            <View style={styles.progressBar}>
              <Animated.View 
                style={[
                  styles.progressFill,
                  {
                    width: progressAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: ['0%', '100%'],
                    }),
                  }
                ]} 
              />
            </View>
            <Text style={styles.progressText}>Verifying your account</Text>
          </View>
        </Animated.View>
      </Animated.View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loaderContainer: {
    backgroundColor: globalConfigurableData.cardBgColor || '#FFFFFF',
    borderRadius: 16,
    padding: 32,
    alignItems: 'center',
    maxWidth: width * 0.85,
    minWidth: 300,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  iconContainer: {
    marginBottom: 24,
    padding: 16,
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    borderRadius: 50,
  },
  title: {
    ...globalFontStyle.appFontTitle,
    color: globalConfigurableData.primaryTextColor1,
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    ...globalFontStyle.appFontSubText,
    color: globalConfigurableData.secondaryTextColor1,
    textAlign: 'center',
    marginBottom: 32,
    lineHeight: 20,
  },
  spinnerContainer: {
    marginBottom: 24,
  },
  progressContainer: {
    width: '100%',
    alignItems: 'center',
  },
  progressBar: {
    width: '100%',
    height: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: 2,
    marginBottom: 12,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: globalConfigurableData.primaryTextColor1,
    borderRadius: 2,
  },
  progressText: {
    ...globalFontStyle.appFontSmallText,
    color: globalConfigurableData.secondaryTextColor1,
    textAlign: 'center',
  },
});

export default MagicLinkAuthLoader; 